import { PARTY_PLACES, partyColourOf, resolveColour } from "odyssey-shared";
import type { PartyMember } from "../../party/members.ts";

/**
 * Which characters in view are the player's party members, for the nameplate's party mark (P2.13-T6, ADR-101 decision
 * 24). The page's roster (`PartyStore`) gives the seats; a plate asks which seat its character's full name holds.
 */

/**
 * The place a name that is no seated member's gives.
 */
export const NO_PARTY_PLACE = -1;

/**
 * The atlas frame of the party mark's fill: a white circle of 4 px, tinted the member's colour.
 */
export const PARTY_MARK_FRAME = "ui/glyph/party-dot_6";

/**
 * The atlas frame of the party mark's rim: the circle's black outline, drawn untinted under the
 * tinted mark so every member colour keeps a dark edge.
 */
export const PARTY_RIM_FRAME = "ui/world/party-dot-rim_6";

/**
 * Each place's map colour as a tint, made once so choosing one allocates nothing.
 */
const PLACE_TINTS: readonly number[] = Object.freeze(Array.from({ length: PARTY_PLACES }, (_, place) => Number.parseInt(resolveColour(partyColourOf(place)).slice(1), 16)));

/**
 * The party mark's tint for a roster place: the colour of that member's mark on the minimap and dot in the party frame.
 *
 * @param place The roster place, 0 to 5.
 * @returns The colour as a tint.
 * @throws RangeError when the place is not a whole number from 0 to 5.
 */
export function partyMarkTint(place: number): number {
  partyColourOf(place);
  return PLACE_TINTS[place] as number;
}

/**
 * Joins a character's two names into the key seats are held under, lower case as the gateway compares them.
 *
 * @param first The first name.
 * @param family The family name.
 * @returns The key.
 */
function keyOf(first: string, family: string): string {
  return `${first.toLowerCase()}\u0000${family.toLowerCase()}`;
}

/**
 * The seated members by full name, with a revision that moves whenever a name gains or loses a place, so a plate can
 * keep what it found and look again only when the revision moves.
 *
 * @example
 * const plates = new PartyPlates();
 * plates.set(snapshot.members);
 * plates.placeOf("Vessa", "Thorn");
 */
export class PartyPlates {
  private places = new Map<string, number>();
  private held = "";
  private count = 0;

  /**
   * The number of changes the seats have had, 0 before any is set.
   *
   * @returns The revision.
   */
  public get revision(): number {
    return this.count;
  }

  /**
   * Takes the roster the page holds. A roster that seats the same names in the same places changes nothing.
   *
   * @param members The seated members, empty while the player is in no party of two or more.
   */
  public set(members: readonly PartyMember[]): void {
    const key = members.map((member) => `${member.place}:${keyOf(member.firstName, member.familyName)}`).join("\u0001");
    if (key === this.held) {
      return;
    }
    this.held = key;
    this.count += 1;
    this.places = new Map(members.map((member) => [keyOf(member.firstName, member.familyName), member.place] as const));
  }

  /**
   * The place a character's full name holds. A name with no family part is no member's.
   *
   * @param first The first name.
   * @param family The family name.
   * @returns The roster place 0 to 5, or `NO_PARTY_PLACE`.
   */
  public placeOf(first: string, family: string): number {
    if (family === "" || this.places.size === 0) {
      return NO_PARTY_PLACE;
    }
    return this.places.get(keyOf(first, family)) ?? NO_PARTY_PLACE;
  }
}
